/**
 * The grammar for a SWADE range value.
 *
 * A range is one of four shapes: an increment triple (`12/24/48`, short/medium/long), a scalar
 * (`12`), an opaque token with an optional multiplier (`Smarts x2`, `Self`, `Touch`), or melee —
 * the absence of range.
 *
 * Melee is accepted on input as `0`, `''` or `null`, and always RESOLVES to the number `0`. It
 * cannot resolve to `null`: the container-configuration layer strips a null value as "no override"
 * at three points, so a null melee would vanish on persist and read back as the unmodified base.
 *
 * Only a scalar or exactly three increments are valid. Two-part ranges are rejected rather than
 * given invented semantics.
 */
export type RangeValue =
    | { kind: 'increments'; increments: [number, number, number] }
    | { kind: 'scalar'; value: number }
    | { kind: 'token'; text: string; multiplier: number }
    | { kind: 'melee' };

const MULTIPLIER_PATTERN: RegExp = /[x*×]\s*(\d+(?:\.\d+)?)\s*$/i;
const NUMERIC_PATTERN: RegExp = /^\d+(?:\.\d+)?$/;

const MELEE: RangeValue = { kind: 'melee' };

function parseNumber(raw: string): number | null {
    if (!NUMERIC_PATTERN.test(raw)) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export function parseRange(raw: unknown): RangeValue | null {
    if (raw === null || raw === undefined) return MELEE;

    if (typeof raw === 'number') {
        if (!Number.isFinite(raw) || raw < 0) return null;
        return raw === 0 ? MELEE : { kind: 'scalar', value: raw };
    }

    const text = String(raw).trim();
    if (text === '') return MELEE;

    if (text.includes('/')) {
        const parts = text.split('/').map((part) => part.trim());
        if (parts.length !== 3) return null;

        const numbers = parts.map(parseNumber);
        if (numbers.some((n) => n === null)) return null;

        const increments = numbers as number[];
        if (increments.every((n) => n === 0)) return MELEE;

        return { kind: 'increments', increments: [increments[0], increments[1], increments[2]] };
    }

    const scalar = parseNumber(text);
    if (scalar !== null) return scalar === 0 ? MELEE : { kind: 'scalar', value: scalar };

    // Anything else is an opaque token, optionally carrying a trailing multiplier.
    let tokenText = text;
    let multiplier = 1;

    const scaled = MULTIPLIER_PATTERN.exec(tokenText);
    if (scaled) {
        multiplier = Number(scaled[1]);
        tokenText = tokenText.slice(0, scaled.index).trim();
    }

    if (tokenText === '') return null;

    // A token whose text is numeric is ambiguous: `'12 x 1'` would become a token that renders as
    // `'12'` and re-parses as a scalar, and the resolver refuses arithmetic on tokens, so the two
    // spellings of the same range would behave differently. Reject rather than pick a reading.
    if (parseNumber(tokenText) !== null) return null;

    return { kind: 'token', text: tokenText, multiplier };
}

export function renderRange(value: RangeValue): string | number {
    switch (value.kind) {
        case 'melee':
            return 0;
        case 'scalar':
            return value.value;
        case 'increments':
            return value.increments.join('/');
        case 'token':
            return value.multiplier === 1 ? value.text : `${value.text} x${value.multiplier}`;
    }
}
